import type { SceneProject } from "@/types/scene";

const API_BASE = "./api";

export interface RemoteUser { id: number; email: string; created_at: string; }
export interface RemoteProjectSummary { id: string; name: string; thumbnail: string | null; created_at: string; updated_at: string; }

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

async function request<T>(path: string, options: RequestInit = {}, csrfToken?: string | null): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body) headers.set("Content-Type", "application/json");
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  let response: Response;
  try { response = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: "same-origin" }); }
  catch { throw new ApiError("Backend is not reachable.", 0); }
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) throw new ApiError("PHP backend is not available on this host.", 0);
  const payload = await response.json() as { error?: string } & T;
  if (!response.ok) throw new ApiError(payload.error ?? "API request failed.", response.status);
  return payload;
}

export const projectApi = {
  me: () => request<{ user: RemoteUser | null; csrfToken: string | null }>("/me"),
  login: (email: string, password: string) => request<{ user: RemoteUser; csrfToken: string }>("/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string) => request<{ user: RemoteUser; csrfToken: string }>("/register", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: (csrfToken: string) => request<{ ok: true }>("/logout", { method: "POST", body: "{}" }, csrfToken),
  listProjects: () => request<{ projects: RemoteProjectSummary[] }>("/projects"),
  getProject: (id: string) => request<{ project: RemoteProjectSummary & { scene: SceneProject } }>(`/projects/${id}`),
  createProject: (project: SceneProject, csrfToken: string) => request<{ project: { id: string; name: string } }>("/projects", { method: "POST", body: JSON.stringify({ name: project.name, scene: project }) }, csrfToken),
  updateProject: (id: string, project: SceneProject, csrfToken: string) => request<{ project: { id: string; name: string } }>(`/projects/${id}`, { method: "PUT", body: JSON.stringify({ name: project.name, scene: project }) }, csrfToken),
  deleteProject: (id: string, csrfToken: string) => request<{ ok: true }>(`/projects/${id}`, { method: "DELETE", body: "{}" }, csrfToken),
};
